/**
 * =============================================================================
 * SAVE BUTTON - Optimized with memoization
 * =============================================================================
 */

import { useState, useEffect, useCallback, useId, memo } from "react";
import { Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { hapticSuccess, hapticSelection } from "@/utils/haptics";

interface SaveButtonProps {
  listingId: string;
  userId: string | undefined;
  size?: "sm" | "default" | "lg";
  variant?: "default" | "ghost" | "outline";
  showCount?: boolean;
  initialIsSaved?: boolean;
}

export const SaveButton = memo(function SaveButton({
  listingId,
  userId,
  size = "default",
  variant = "ghost",
  showCount = true,
  initialIsSaved
}: SaveButtonProps) {
  const [isSaved, setIsSaved] = useState(initialIsSaved ?? false);
  const [loading, setLoading] = useState(false);
  const [saveCount, setSaveCount] = useState(0);
  const { toast } = useToast();
  // Unikt id per instans – annars krockar två knappar för samma annons på samma sida
  const instanceId = useId();

  useEffect(() => {
    if (initialIsSaved !== undefined) {
      setIsSaved(initialIsSaved);
    }
  }, [initialIsSaved]);

  useEffect(() => {
    fetchSaveCount();
    
    if (initialIsSaved === undefined && userId) {
      checkIfSaved();
    }

    if (!userId) return;

    const channel = supabase
      .channel(`listing-saves-${listingId}:${userId}:${instanceId}`, { config: { private: true } })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'saved_listings',
        filter: `listing_id=eq.${listingId}`
      }, fetchSaveCount)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, listingId, instanceId]);

  const fetchSaveCount = useCallback(async () => {
    const { count } = await supabase
      .from('saved_listings')
      .select('*', { count: 'exact', head: true })
      .eq('listing_id', listingId);
    setSaveCount(count || 0);
  }, [listingId]);

  const checkIfSaved = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('saved_listings')
      .select('id')
      .eq('user_id', userId)
      .eq('listing_id', listingId)
      .maybeSingle();
    setIsSaved(!!data);
  }, [userId, listingId]);

  const handleToggleSave = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!userId) {
      toast({
        title: "Logga in krävs",
        description: "Du måste vara inloggad för att spara annonser",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    // Optimistic update
    const wasIsSaved = isSaved;
    setIsSaved(!wasIsSaved);
    setSaveCount(prev => wasIsSaved ? Math.max(0, prev - 1) : prev + 1);

    try {
      if (wasIsSaved) {
        const { error } = await supabase
          .from('saved_listings')
          .delete()
          .eq('user_id', userId)
          .eq('listing_id', listingId);
        
        if (error) throw error;
        hapticSelection();
        toast({
          title: "Borttagen",
          description: "Annonsen har tagits bort från sparade"
        });
      } else {
        const { error } = await supabase
          .from('saved_listings')
          .insert({ user_id: userId, listing_id: listingId });
        
        if (error) throw error;
        hapticSuccess();
        toast({
          title: "Sparad!",
          description: "Annonsen har sparats"
        });
      }
    } catch (error: any) {
      // Rollback on error
      setIsSaved(wasIsSaved);
      setSaveCount(prev => wasIsSaved ? prev + 1 : Math.max(0, prev - 1));
      toast({
        title: "Ett fel uppstod",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [userId, listingId, isSaved, toast]);

  return (
    <div className="flex items-center gap-2">
      {showCount}
      <Button
        variant={variant}
        size={size === "sm" ? "sm" : "icon"}
        onClick={handleToggleSave}
        disabled={loading}
        className="relative"
        aria-label={isSaved ? 'Ta bort från sparade' : 'Spara annons'}
      >
        <Heart 
          className={`h-4 w-4 transition-colors duration-200 ${
            isSaved ? "fill-primary text-primary" : "text-muted-foreground"
          }`} 
        />
      </Button>
    </div>
  );
});